import React from 'react';

type IntentRule = {
  id: string;
  label: string;
  tone: string;
  description: string;
  words: string[];
};

type IntentMatch = {
  rule: IntentRule;
  suggestions: string[];
  triggers: string[];
};

type Props = {
  suggestions: string[];
  onKeywordClick?: (keyword: string) => void;
};

const INTENT_RULES: IntentRule[] = [
  {
    id: 'pain',
    label: '悩み・不安',
    tone: 'bg-rose-50 text-rose-700 border-rose-100',
    description: '困りごとや苦手意識',
    words: ['わからない', '分からない', 'できない', '難しい', '不安', '困る', '苦手', '嫌い', 'つらい', '無理'],
  },
  {
    id: 'comparison',
    label: '比較・検討',
    tone: 'bg-amber-50 text-amber-700 border-amber-100',
    description: '選定前の比較行動',
    words: ['おすすめ', '比較', 'ランキング', 'どれ', '選び方', '違い', '人気', 'ベスト', 'おすすめの'],
  },
  {
    id: 'price',
    label: '価格感度',
    tone: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    description: '無料・料金への反応',
    words: ['無料', '安い', '料金', '費用', '価格', '高い', '月額', '値段', 'コスパ'],
  },
  {
    id: 'purchase',
    label: '購買・導入',
    tone: 'bg-blue-50 text-blue-700 border-blue-100',
    description: '購入や利用開始の意図',
    words: ['買う', '購入', '申し込み', '登録', '始め方', 'アプリ', '教材', '本', '参考書', 'サービス'],
  },
  {
    id: 'howto',
    label: '方法・やり方',
    tone: 'bg-violet-50 text-violet-700 border-violet-100',
    description: '手順や学び方の探索',
    words: ['方法', 'やり方', '使い方', '勉強法', '覚え方', '解き方', 'とは', '作り方', '始め方', 'コツ'],
  },
  {
    id: 'reputation',
    label: '口コミ・評判',
    tone: 'bg-sky-50 text-sky-700 border-sky-100',
    description: '第三者評価の確認',
    words: ['口コミ', '評判', 'レビュー', '感想', '体験談', '評価', '炎上'],
  },
  {
    id: 'risk',
    label: 'リスク・不満',
    tone: 'bg-slate-50 text-slate-700 border-slate-100',
    description: '失敗回避や離脱意図',
    words: ['解約', '退会', '危険', '怪しい', '悪い', '失敗', '後悔', 'デメリット', '注意'],
  },
];

const normalize = (value: string) => value.trim().toLowerCase();

const hasIntentWord = (suggestion: string, word: string) => {
  const normalizedSuggestion = normalize(suggestion);
  const normalizedWord = normalize(word);
  if (!normalizedWord) return false;

  if (normalizedWord.length <= 2) {
    return normalizedSuggestion
      .split(/\s+/)
      .some((part) => part === normalizedWord);
  }

  return normalizedSuggestion.includes(normalizedWord);
};

const IntentAnalysisCard: React.FC<Props> = ({ suggestions, onKeywordClick }) => {
  const uniqueSuggestions = React.useMemo(
    () => suggestions.map((word) => word.trim()).filter((word, index, list) => word && list.indexOf(word) === index),
    [suggestions]
  );

  const matches = React.useMemo<IntentMatch[]>(() => (
    INTENT_RULES.map((rule, ruleIndex) => {
      const matchedSuggestions = uniqueSuggestions.filter((suggestion) => {
        return rule.words.some((word) => hasIntentWord(suggestion, word));
      });

      const triggers = rule.words.filter((word) => (
        matchedSuggestions.some((suggestion) => hasIntentWord(suggestion, word))
      ));

      return {
        rule,
        suggestions: matchedSuggestions,
        triggers,
        ruleIndex,
      };
    })
      .filter((match) => match.suggestions.length > 0)
      .sort((a, b) => (b.suggestions.length - a.suggestions.length) || (a.ruleIndex - b.ruleIndex))
      .map(({ ruleIndex, ...match }) => match)
  ), [uniqueSuggestions]);

  if (uniqueSuggestions.length === 0) return null;

  const matchedTotal = new Set(matches.flatMap((match) => match.suggestions)).size;
  const strongest = matches[0];

  return (
    <section className="bg-white shadow-sm rounded-lg p-6 min-w-0">
      <div className="flex flex-col gap-3 border-b pb-2 mb-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="font-bold text-gray-800">感情・意図分析 β</h2>
          <p className="mt-1 text-[11px] text-gray-500">固定辞書でサジェストの検索意図を分類</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold px-2 py-1 rounded bg-blue-50 text-blue-700">
            {matchedTotal.toLocaleString('ja-JP')}語にシグナル
          </span>
          {strongest && (
            <span className={`text-xs font-semibold px-2 py-1 rounded border ${strongest.rule.tone}`}>
              最大: {strongest.rule.label}
            </span>
          )}
        </div>
      </div>

      {matches.length === 0 ? (
        <div className="h-[120px] flex items-center justify-center text-center text-sm text-gray-500">
          いまの辞書では強い感情・意図シグナルは見つかりませんでした
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
          {matches.map((match) => (
            <div key={match.rule.id} className="rounded border border-gray-100 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className={`inline-flex rounded border px-2 py-1 text-xs font-bold ${match.rule.tone}`}>
                    {match.rule.label}
                  </span>
                  <p className="mt-2 text-xs text-gray-500">{match.rule.description}</p>
                </div>
                <span className="text-2xl font-bold text-gray-800">{match.suggestions.length}</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-1">
                {match.triggers.slice(0, 5).map((trigger) => (
                  <span key={trigger} className="rounded bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-600">
                    {trigger}
                  </span>
                ))}
              </div>

              <div className="mt-3 space-y-1">
                {match.suggestions.slice(0, 4).map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => onKeywordClick?.(suggestion)}
                    className="block w-full rounded px-2 py-1 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default IntentAnalysisCard;
